import React, { useEffect, useState, useCallback } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Headphones, Plus, Trash2, Pencil, Upload, Mic, Loader2, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';

const API = '';

const EMPTY_FORM = {
  episode_id: null,
  title: '',
  description: '',
  guest: '',
  episode_number: '',
  duration_seconds: '',
  external_url: '',
  tags: '',
  status: 'published',
  audio: null,
};

function formatBytes(bytes) {
  if (!bytes) return '';
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export default function AdminBriteCastTab() {
  const [episodes, setEpisodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/admin/britecast`, { credentials: 'include' });
      if (res.ok) setEpisodes(await res.json());
    } catch {}
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setForm(EMPTY_FORM); setOpen(true); };
  const openEdit = (ep) => {
    setForm({
      episode_id: ep.episode_id,
      title: ep.title || '',
      description: ep.description || '',
      guest: ep.guest || '',
      episode_number: ep.episode_number || '',
      duration_seconds: ep.duration_seconds || '',
      external_url: ep.external_url || '',
      tags: (ep.tags || []).join(', '),
      status: ep.status || 'published',
      audio: null,
    });
    setOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) {
      toast.error('Title is required');
      return;
    }
    if (!form.audio && !form.external_url.trim() && !form.episode_id) {
      toast.error('Provide an audio file or an external URL');
      return;
    }
    setSaving(true);
    const fd = new FormData();
    fd.append('title', form.title);
    fd.append('description', form.description);
    fd.append('guest', form.guest);
    if (form.episode_number) fd.append('episode_number', form.episode_number);
    if (form.duration_seconds) fd.append('duration_seconds', form.duration_seconds);
    fd.append('external_url', form.external_url);
    fd.append('tags', form.tags);
    fd.append('status', form.status);
    if (form.audio) fd.append('audio', form.audio);
    try {
      const url = form.episode_id
        ? `${API}/api/admin/britecast/${form.episode_id}`
        : `${API}/api/admin/britecast`;
      const res = await fetch(url, {
        method: form.episode_id ? 'PUT' : 'POST',
        credentials: 'include',
        body: fd,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed');
      toast.success(form.episode_id ? 'Episode updated' : 'Episode created');
      setOpen(false);
      load();
    } catch (err) {
      toast.error(err.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (ep) => {
    // eslint-disable-next-line no-restricted-globals
    if (!window.confirm(`Delete episode "${ep.title}"? This also removes the audio file.`)) return;
    try {
      const res = await fetch(`${API}/api/admin/britecast/${ep.episode_id}`, {
        method: 'DELETE', credentials: 'include',
      });
      if (!res.ok) throw new Error('Failed');
      toast.success('Episode deleted');
      setEpisodes(prev => prev.filter(e => e.episode_id !== ep.episode_id));
    } catch (err) {
      toast.error(err.message || 'Delete failed');
    }
  };

  return (
    <div className="space-y-4" data-testid="admin-britecast-tab">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <Headphones className="h-4 w-4 text-brand-navy" /> BriteCast
          </h3>
          <p className="text-sm text-slate-500">Upload and manage podcast episodes shown on the member BriteCast page.</p>
        </div>
        <Button onClick={openCreate} data-testid="britecast-new-btn"
          className="bg-brand-navy hover:bg-brand-dark text-white">
          <Plus className="h-4 w-4 mr-1.5" /> New episode
        </Button>
      </div>

      {loading ? (
        <Card className="p-8 text-center text-sm text-slate-500">
          <Loader2 className="h-5 w-5 animate-spin inline mr-2" /> Loading…
        </Card>
      ) : episodes.length === 0 ? (
        <Card className="p-12 text-center" data-testid="britecast-admin-empty">
          <Mic className="h-10 w-10 text-slate-300 mx-auto mb-3" />
          <p className="text-sm text-slate-600 font-medium">No episodes yet</p>
          <p className="text-xs text-slate-400 mt-1">Click "New episode" to upload your first BriteCast.</p>
        </Card>
      ) : (
        <Card className="divide-y" data-testid="britecast-admin-list">
          {episodes.map(ep => (
            <div key={ep.episode_id} className="px-4 py-3 flex items-start gap-3"
              data-testid={`britecast-admin-row-${ep.episode_id}`}>
              <div className="w-10 h-10 rounded-md bg-gradient-to-br from-brand-navy to-brand-sage flex items-center justify-center text-white flex-shrink-0">
                <Mic className="h-4 w-4" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="text-sm font-semibold text-slate-800 truncate">{ep.title}</p>
                  {ep.status === 'draft' && (
                    <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200 text-[10px]">Draft</Badge>
                  )}
                  {ep.episode_number && (
                    <Badge variant="outline" className="text-[10px]">Ep {ep.episode_number}</Badge>
                  )}
                </div>
                <p className="text-xs text-slate-500 mt-0.5 truncate">
                  {ep.guest ? `With ${ep.guest} · ` : ''}
                  {ep.has_audio_file ? `${ep.audio_filename || 'audio'} · ${formatBytes(ep.audio_size)}` :
                    ep.external_url ? <>External URL <ExternalLink className="h-2.5 w-2.5 inline" /></> : 'No audio'}
                  {ep.published_at ? ` · ${new Date(ep.published_at).toLocaleDateString()}` : ''}
                </p>
              </div>
              <div className="flex items-center gap-1 flex-shrink-0">
                <Button size="sm" variant="ghost" onClick={() => openEdit(ep)}
                  data-testid={`britecast-edit-${ep.episode_id}`}
                  className="h-8 w-8 p-0 text-slate-500 hover:text-brand-navy">
                  <Pencil className="h-3.5 w-3.5" />
                </Button>
                <Button size="sm" variant="ghost" onClick={() => handleDelete(ep)}
                  data-testid={`britecast-delete-${ep.episode_id}`}
                  className="h-8 w-8 p-0 text-rose-500 hover:text-rose-700 hover:bg-rose-50">
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>
          ))}
        </Card>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto" data-testid="britecast-form-dialog">
          <DialogHeader>
            <DialogTitle>{form.episode_id ? 'Edit episode' : 'New episode'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="bc-title">Title *</Label>
              <Input id="bc-title" data-testid="britecast-form-title" required
                value={form.title} onChange={e => setForm({...form, title: e.target.value})}
                className="mt-1.5" placeholder="The Future of Last-Mile Logistics" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="bc-guest">Guest</Label>
                <Input id="bc-guest" data-testid="britecast-form-guest"
                  value={form.guest} onChange={e => setForm({...form, guest: e.target.value})}
                  className="mt-1.5" placeholder="e.g. Jane Smith" />
              </div>
              <div>
                <Label htmlFor="bc-num">Episode #</Label>
                <Input id="bc-num" type="number" data-testid="britecast-form-number"
                  value={form.episode_number} onChange={e => setForm({...form, episode_number: e.target.value})}
                  className="mt-1.5" />
              </div>
            </div>
            <div>
              <Label htmlFor="bc-desc">Description</Label>
              <Textarea id="bc-desc" data-testid="britecast-form-description" rows={4}
                value={form.description} onChange={e => setForm({...form, description: e.target.value})}
                className="mt-1.5" placeholder="A short summary of what listeners will get…" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="bc-tags">Tags (comma-separated)</Label>
                <Input id="bc-tags" data-testid="britecast-form-tags"
                  value={form.tags} onChange={e => setForm({...form, tags: e.target.value})}
                  className="mt-1.5" placeholder="Sustainability, Logistics" />
              </div>
              <div>
                <Label htmlFor="bc-status">Status</Label>
                <select id="bc-status" data-testid="britecast-form-status"
                  value={form.status} onChange={e => setForm({...form, status: e.target.value})}
                  className="mt-1.5 w-full h-10 rounded-md border border-slate-200 px-3 text-sm">
                  <option value="published">Published</option>
                  <option value="draft">Draft</option>
                </select>
              </div>
            </div>

            <div className="rounded-md bg-slate-50 border border-slate-200 p-3 space-y-3">
              <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">Audio</p>
              <div>
                <Label htmlFor="bc-audio" className="text-sm flex items-center gap-1.5">
                  <Upload className="h-3.5 w-3.5" /> Upload audio file
                </Label>
                <Input id="bc-audio" type="file" accept="audio/*"
                  data-testid="britecast-form-audio"
                  onChange={e => setForm({...form, audio: e.target.files?.[0] || null})}
                  className="mt-1.5 file:bg-brand-navy file:text-white file:border-0 file:px-3 file:py-1 file:rounded" />
                <p className="text-[10px] text-slate-500 mt-1">MP3 / M4A / WAV / OGG, max 200 MB. {form.episode_id ? 'Leave blank to keep existing audio.' : ''}</p>
              </div>
              <div className="text-center text-xs text-slate-400">or</div>
              <div>
                <Label htmlFor="bc-ext">External URL (Spotify / Anchor / YouTube)</Label>
                <Input id="bc-ext" data-testid="britecast-form-external"
                  value={form.external_url} onChange={e => setForm({...form, external_url: e.target.value})}
                  className="mt-1.5" placeholder="https://open.spotify.com/episode/…" />
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setOpen(false)} disabled={saving}>
                Cancel
              </Button>
              <Button type="submit" disabled={saving}
                data-testid="britecast-form-submit"
                className="bg-brand-navy hover:bg-brand-dark text-white">
                {saving ? 'Saving…' : (form.episode_id ? 'Update episode' : 'Create episode')}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
